import React, { useEffect, useState } from "react";
import { campaignsApi, Campaign, Performance } from "../../../api/maintenance/campaigns";
import { money, num, ts, errMsg } from "./campaignUtils";
import { useNotice } from "../shared/useNotice";

interface Props {
  campaigns: Campaign[];
}

const PerformanceTab: React.FC<Props> = ({ campaigns }) => {
  const eligible = campaigns.filter((c) => c.status === "live" || c.status === "ended");
  const [sel, setSel] = useState<number | null>(eligible[0]?.id ?? null);
  const [perf, setPerf] = useState<Performance | null>(null);
  const [err, setErr] = useNotice("error", "");

  useEffect(() => { if (sel == null && eligible.length) setSel(eligible[0].id); }, [eligible, sel]);
  useEffect(() => {
    if (sel == null) return;
    setPerf(null); setErr("");
    campaignsApi.performance(sel).then((r) => setPerf(r.data)).catch((e) => setErr(errMsg(e, "Failed to load performance")));
  }, [sel]);

  const c = campaigns.find((x) => x.id === sel) || null;
  const f = perf?.forecast || null;
  const settled = perf?.by_state?.settled || { count: 0, amount: 0 };
  const fcWindow = f?.window.payout ?? null;
  const diff = fcWindow != null && fcWindow > 0 ? ((settled.amount - fcWindow) / fcWindow) * 100 : null;
  const dayMax = Math.max(1, ...(perf?.per_day || []).map((d) => d.payout));

  return (
    <>
      <div className="ma-toolbar">
        <label className="ma-select">Campaign
          <select value={sel ?? ""} onChange={(e) => setSel(Number(e.target.value))}>
            {!eligible.length && <option value="">No live or ended campaign yet</option>}
            {eligible.map((x) => <option key={x.id} value={x.id}>{x.name} · {x.status}</option>)}
          </select>
        </label>
        {c && <span className="ma-hint">{ts(c.starts_at)} → {ts(c.ends_at)} · {c.status}</span>}
      </div>
      {err && <div className="ma-error">{err}</div>}
      {!eligible.length && <div className="ma-note">Performance appears once a campaign has gone live. Forecast-against-actual is only answerable because the forecast is frozen at schedule time.</div>}

      {perf && c && (<>
        <div className="ma-kpis">
          <div className="ma-kpi"><div className="ma-kpi-label">Forecast</div><div className="ma-kpi-value">{fcWindow != null ? money(fcWindow) : "—"}</div><div className="ma-kpi-sub">frozen when scheduled</div></div>
          <div className="ma-kpi"><div className="ma-kpi-label">Actually paid</div><div className="ma-kpi-value">{money(settled.amount)}</div>
            <div className="ma-kpi-sub">{diff == null ? `${num(settled.count)} rewards` : `${diff >= 0 ? "+" : ""}${diff.toFixed(0)}% vs forecast`}</div></div>
          <div className="ma-kpi"><div className="ma-kpi-label">Rewards</div><div className="ma-kpi-value">{num(perf.counters.awarded_count)}</div>
            <div className="ma-kpi-sub">{perf.counters.max_awards ? `of ${num(perf.counters.max_awards)} max` : f ? `forecast ${num(f.window.topups)}` : ""}</div></div>
          <div className="ma-kpi"><div className="ma-kpi-label">People</div><div className="ma-kpi-value">{num(perf.users.users)}</div><div className="ma-kpi-sub">{num(perf.users.first_time_users)} first top-up ever</div></div>
          <div className="ma-kpi"><div className="ma-kpi-label">Clawed back</div><div className="ma-kpi-value">{money(perf.clawed_back)}</div><div className="ma-kpi-sub">refunds, forward-dated</div></div>
        </div>

        <div className="ma-grid">
          <div className="ma-card half">
            <div className="ma-card-h"><h3>Forecast against actual, by slot</h3></div>
            <div className="ma-tablewrap"><table className="ma-table">
              <thead><tr><th>Slot</th><th className="num">Forecast</th><th className="num">Actual</th><th className="num">Rewards</th><th className="num">Diff</th></tr></thead>
              <tbody>
                {(f?.per_slot || []).map((s) => {
                  const a = perf.per_slot.find((p) => p.slot === s.slot);
                  const fc = s.payout * ((f?.window_days || 0) / (f?.history_days || 1));
                  const d = fc > 0 && a ? ((a.payout - fc) / fc) * 100 : null;
                  return (<tr key={s.slot}><td>{s.slot}</td><td className="num">{money(fc)}</td><td className="num">{money(a?.payout ?? 0)}</td><td className="num">{num(a?.n ?? 0)}</td>
                    <td className="num" style={{ color: d == null ? undefined : d > 0 ? "#e65100" : "#2e7d32" }}>{d == null ? "—" : `${d >= 0 ? "+" : ""}${d.toFixed(0)}%`}</td></tr>);
                })}
                {perf.per_slot.filter((p) => !(f?.per_slot || []).some((s) => s.slot === p.slot)).map((p) => (
                  <tr key={p.slot || "?"}><td>{p.slot || "—"}</td><td className="num">—</td><td className="num">{money(p.payout)}</td><td className="num">{num(p.n)}</td><td className="num">—</td></tr>
                ))}
                {!f && !perf.per_slot.length && <tr><td colSpan={5} className="ma-empty">Nothing paid yet</td></tr>}
              </tbody>
            </table></div>
          </div>

          <div className="ma-card half">
            <div className="ma-card-h"><h3>Paid per day</h3></div>
            <div className="ma-card-b">
              <div className="ma-bars">
                {perf.per_day.map((d) => (
                  <div className="ma-bar" key={String(d.day)} title={`${d.n} rewards`}>
                    <span className="nm">{String(d.day).slice(0, 10)}</span>
                    <span className="track"><span className="fill" style={{ width: `${(d.payout / dayMax) * 100}%` }} /></span>
                    <span className="val">{money(d.payout)}</span>
                  </div>
                ))}
                {!perf.per_day.length && <div className="ma-empty">Nothing paid yet</div>}
              </div>
            </div>
          </div>

          <div className="ma-card">
            <div className="ma-card-h"><h3>Limits and states</h3></div>
            <div className="ma-card-b" style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              {Object.entries(perf.by_state).map(([k, v]) => <span key={k} className={`ma-pill ${k === "settled" ? "ok" : k === "rejected" ? "bad" : "warn"}`}>{k}: {num(v.count)} · {money(v.amount)}</span>)}
              {Object.entries(perf.limit_events).map(([k, v]) => <span key={k} className="ma-pill info">{k.replace(/_/g, " ")} ×{v}</span>)}
              {/* Every limit the campaign actually carries. Only the budget used to
                  render, so a campaign capped at 100 recharges with one reward per
                  person showed a card headed "Limits" with no limit on it. */}
              {perf.counters.budget_cap != null && <span className="ma-pill muted">budget {money(perf.counters.awarded_amount)} of {money(perf.counters.budget_cap)}</span>}
              {perf.counters.max_awards != null && <span className="ma-pill muted">{num(perf.counters.awarded_count)} of {num(perf.counters.max_awards)} rewards</span>}
              {perf.counters.per_user_award_limit != null && (
                <span className="ma-pill muted">
                  {num(perf.counters.per_user_award_limit)} per person
                  {perf.counters.per_user_reset !== "campaign" && ` / ${perf.counters.per_user_reset}`}
                </span>
              )}
              {perf.counters.per_user_cap != null && <span className="ma-pill muted">{money(perf.counters.per_user_cap)} per person</span>}
              {perf.counters.max_cashback_cap != null && <span className="ma-pill muted">{money(perf.counters.max_cashback_cap)} per reward</span>}
              {perf.counters.budget_cap == null && perf.counters.max_awards == null
                && perf.counters.per_user_award_limit == null && perf.counters.per_user_cap == null
                && perf.counters.max_cashback_cap == null && (
                <span className="ma-pill muted">no limits — time only</span>
              )}
            </div>
          </div>
        </div>
      </>)}
    </>
  );
};

export default PerformanceTab;
